/**
 * ScheduledMessagesPanel
 * What is queued to go out for one client or one lead, with the controls to
 * pause, resume or cancel it.
 *
 * This panel is the reason the queue is usable at all. Without it a message
 * scheduled for next month is invisible from the moment it is created until the
 * moment it arrives — the sender has no way to check what they set up, change
 * their mind, or notice that it was withheld. The Outbox on Admin → Email is the
 * same list across every record; this is the one that belongs on the record.
 *
 * Cancelled and sent messages are hidden by default, behind a toggle: the
 * question this answers is "what is about to go out", and a year of history
 * would bury the one line that matters.
 */
import { useState, useEffect, useCallback } from 'react'
import {
  listScheduledMessages,
  updateScheduledMessage,
  deleteScheduledMessage,
} from '../services/crm'
import { formatLocalDateTime } from '../utils/formatDateTime'
import type { ScheduledMessage, ScheduledMessageStatus } from '../types'

interface Props {
  clientId?: number
  leadId?: number
  /** Bumped by the parent after a send, to re-read the list. */
  refreshKey?: number
  /** Rendered in the header — normally the "Schedule a message" button. */
  action?: React.ReactNode
}

const STATUS_STYLES: Record<ScheduledMessageStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-700',
  paused: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600',
}

function statusLabel(m: ScheduledMessage): string {
  if (m.status === 'scheduled') return 'Waiting'
  if (m.status === 'paused') return 'Paused'
  if (m.status === 'sent') return m.sent_count > 1 ? `Sent ${m.sent_count}×` : 'Sent'
  if (m.status === 'failed') return 'Failed'
  return 'Cancelled'
}

export default function ScheduledMessagesPanel({ clientId, leadId, refreshKey, action }: Props) {
  const [messages, setMessages] = useState<ScheduledMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [showFinished, setShowFinished] = useState(false)
  const [error, setError] = useState('')
  const [working, setWorking] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setMessages(
        await listScheduledMessages({
          client_id: clientId,
          lead_id: leadId,
          include_finished: showFinished,
        })
      )
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load scheduled messages')
    } finally {
      setLoading(false)
    }
  }, [clientId, leadId, showFinished])

  useEffect(() => { void load() }, [load, refreshKey])

  async function change(id: number, status: 'scheduled' | 'paused' | 'cancelled') {
    setWorking(id)
    setError('')
    try {
      await updateScheduledMessage(id, status)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the message')
    } finally {
      setWorking(null)
    }
  }

  async function remove(id: number) {
    setWorking(id)
    setError('')
    try {
      await deleteScheduledMessage(id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the message')
    } finally {
      setWorking(null)
    }
  }

  return (
    <div className="mt-6 bg-white rounded-lg shadow">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b">
        <h2 className="text-base font-semibold text-gray-900">
          Scheduled Messages
          <span className="ml-2 text-xs font-normal text-gray-500">({messages.length})</span>
        </h2>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={showFinished}
              onChange={(e) => setShowFinished(e.target.checked)}
            />
            Show finished
          </label>
          {action}
        </div>
      </div>

      {error && <p className="px-5 py-2 text-sm text-red-600">{error}</p>}

      <div className="divide-y divide-gray-100">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-6">
            {showFinished ? 'No messages have been scheduled.' : 'Nothing is waiting to go out.'}
          </p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_STYLES[m.status]}`}>
                    {statusLabel(m)}
                  </span>
                  <span className="text-gray-900 font-medium truncate">
                    {m.template_name ?? 'Message'}
                  </span>
                  {m.recurrence !== 'none' && (
                    <span className="text-xs text-gray-500">· {m.recurrence_summary}</span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {m.next_send_at
                    ? `Next: ${formatLocalDateTime(m.next_send_at)}`
                    : m.last_sent_at
                      ? `Last sent ${formatLocalDateTime(m.last_sent_at)}`
                      : '—'}
                </span>
              </div>

              <div className="text-xs text-gray-500 mt-0.5">To: {m.to_email}</div>

              {m.last_error && (
                <p
                  className={`text-xs mt-0.5 ${
                    m.status === 'cancelled' ? 'text-gray-500' : 'text-red-500'
                  }`}
                >
                  {m.last_error}
                </p>
              )}

              <div className="flex flex-wrap gap-3 mt-1.5">
                {m.status === 'scheduled' && (
                  <button
                    type="button"
                    className="tap text-xs text-primary-600 hover:underline"
                    disabled={working === m.id}
                    onClick={() => change(m.id, 'paused')}
                  >
                    Pause
                  </button>
                )}
                {m.status === 'paused' && (
                  <button
                    type="button"
                    className="tap text-xs text-primary-600 hover:underline"
                    disabled={working === m.id}
                    onClick={() => change(m.id, 'scheduled')}
                  >
                    Resume
                  </button>
                )}
                {(m.status === 'scheduled' || m.status === 'paused') && (
                  <button
                    type="button"
                    className="tap text-xs text-red-600 hover:underline"
                    disabled={working === m.id}
                    onClick={() => change(m.id, 'cancelled')}
                  >
                    Cancel
                  </button>
                )}
                {(m.status === 'sent' || m.status === 'failed' || m.status === 'cancelled') && (
                  <button
                    type="button"
                    className="tap text-xs text-gray-500 hover:underline"
                    disabled={working === m.id}
                    onClick={() => remove(m.id)}
                  >
                    Remove from list
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
